<template>
  <section class="section section-observed announcements-page">
    <div v-if="showSectionHeading" class="section-heading">
      <div>
        <h2>{{ heading }}</h2>
      </div>
    </div>

    <div class="announcement-panel">
      <div class="announcement-panel-head">
        <strong>公告列表</strong>
        <span>共 {{ total }} 条</span>
      </div>

      <div v-if="loading" class="muted-copy panel-empty">正在加载公告...</div>
      <div v-else-if="!items.length" class="empty-panel">
        <p class="muted-copy">{{ emptyHint }}</p>
        <button
          v-if="needsLogin"
          type="button"
          class="primary-action compact"
          style="margin-top: 12px"
          @click="front.openAuth('login')"
        >去登录</button>
      </div>
      <div v-else class="announcement-list">
        <article v-for="(item, index) in items" :key="item.entryId || item.title" class="announcement-card">
          <button type="button" class="announcement-main" @click="openItem(item)">
            <div class="article-index" aria-label="序号">
              <strong>{{ itemIndex(index) }}</strong>
            </div>
            <div class="article-body">
              <div class="announcement-meta">
                <span v-if="item.category">{{ item.category }}</span>
                <time>{{ formatDate(item.date) }}</time>
              </div>
              <h3>{{ item.title }}</h3>
              <p v-if="item.summary" class="announcement-summary">{{ item.summary }}</p>
            </div>
          </button>
          <div class="announcement-actions">
            <button type="button" class="ghost-action compact" @click="openItem(item)">查看详情</button>
            <button
              v-if="item.fileUrl"
              type="button"
              class="primary-action compact"
              @click.stop="front.downloadSiteFile(item.fileUrl, item.title)"
            >
              下载附件
            </button>
            <span v-else-if="needsLoginHint(item)" class="login-hint">附件需登录后查看</span>
          </div>
        </article>
      </div>

      <FrontPagination
        :page-num="pageNum"
        :page-size="pageSize"
        :total="total"
        @change="onPageChange"
      />
    </div>
  </section>
</template>

<script>
import { listPublicCmsEntries, unwrapPublicPage } from '../../api/paleo'
import FrontPagination from '../../components/common/FrontPagination.vue'

export default {
  name: 'AnnouncementsPage',
  components: { FrontPagination },
  inject: ['front'],
  data() {
    return {
      loading: false,
      remoteItems: [],
      pageNum: 1,
      pageSize: 5,
      total: 0,
      loadSeq: 0
    }
  },
  computed: {
    heading() {
      const meta = this.front.pageMeta('society-announcements')
      return meta.pageTitle || '会员公告与组织工作通知'
    },
    showSectionHeading() {
      const title = String(this.heading || '').trim()
      const pageTitle = String(this.front.pageTitle || '').trim()
      return title && title !== pageTitle && title !== '会员公告'
    },
    items() {
      return this.remoteItems
    },
    needsLogin() {
      return !this.front.isLoggedIn
    },
    emptyHint() {
      return this.needsLogin ? '会员公告需登录后查看' : '暂无已发布公告'
    }
  },
  mounted() {
    this.loadAnnouncements()
  },
  methods: {
    itemIndex(index) {
      return (this.pageNum - 1) * this.pageSize + index + 1
    },
    onPageChange(page) {
      this.pageNum = page
      this.loadAnnouncements()
    },
    async loadAnnouncements() {
      this.loading = true
      const seq = ++this.loadSeq
      try {
        const result = await listPublicCmsEntries({
          moduleCode: 'announcements',
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        if (seq !== this.loadSeq) return
        const { rows, total } = unwrapPublicPage(result, {
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        this.remoteItems = rows.map(item => this.front.mapCmsEntry(item))
        this.total = total
      } catch (error) {
        if (seq !== this.loadSeq) return
        // 保留上一页数据；首次失败则清空
        if (!this.remoteItems.length) {
          this.remoteItems = []
          this.total = 0
        }
      } finally {
        if (seq === this.loadSeq) this.loading = false
      }
    },
    needsLoginHint(item) {
      return !item.fileUrl && String(item.memberOnly) === '1'
    },
    openItem(item) {
      if (item.entryId) {
        this.front.goPath(`/content/${item.entryId}`)
        return
      }
      this.front.openContentDetail(this.front.mapCmsEntryToContent(item))
    },
    formatDate(date) {
      const raw = String(date || '').trim()
      return raw ? raw.slice(0, 10) : '待发布'
    }
  }
}
</script>

<style scoped>
.announcements-page {
  max-width: var(--content-max);
}

.announcement-panel {
  padding: 24px 28px 28px;
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  background: #fff;
  box-shadow: var(--shadow-soft, 0 8px 24px rgba(0, 43, 73, 0.06));
}

.announcement-panel-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border);
}

.announcement-panel-head strong {
  color: var(--primary);
  font-size: 16px;
}

.announcement-panel-head span {
  color: var(--subtle);
  font-size: 14px;
}

.announcement-list {
  display: grid;
  gap: 14px;
}

.announcement-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  padding: 16px 18px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fbfdff;
}

.announcement-main {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  gap: 18px;
  align-items: center;
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.article-index {
  display: grid;
  place-items: center;
  min-width: 68px;
  min-height: 68px;
  border-radius: 8px;
  background: linear-gradient(180deg, #eef6ff, #f8fbff);
  color: var(--primary);
}

.article-index strong {
  font-size: 26px;
  line-height: 1;
  font-weight: 800;
}

.article-body {
  min-width: 0;
}

.announcement-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
  color: var(--subtle);
  font-size: 12px;
}

.announcement-meta span {
  display: inline-flex;
  min-height: 22px;
  align-items: center;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--gold-soft);
  color: var(--primary);
  font-weight: 800;
}

.announcement-card h3 {
  margin: 0;
  color: var(--ink, var(--primary));
  font-size: 18px;
  line-height: 1.4;
}

.announcement-summary {
  margin: 8px 0 0;
  color: var(--subtle);
  font-size: 14px;
  line-height: 1.65;
}

.announcement-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
}

.login-hint {
  color: var(--secondary);
  font-size: 13px;
  font-weight: 700;
}

.panel-empty,
.empty-panel {
  padding: 40px 20px;
  text-align: center;
}

.empty-panel {
  border: 1px dashed var(--border);
  border-radius: var(--front-radius);
  background: #fff;
}

a.primary-action {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.announcement-card:hover {
  border-color: rgba(26, 90, 147, 0.35);
  box-shadow: 0 14px 34px rgba(0, 29, 54, 0.08);
}

@media (max-width: 720px) {
  .announcement-panel {
    padding: 18px;
  }

  .announcement-card {
    grid-template-columns: 1fr;
  }

  .announcement-main {
    grid-template-columns: 1fr;
  }

  .article-index {
    display: inline-flex;
    justify-content: center;
    gap: 8px;
    width: fit-content;
    min-height: auto;
    padding: 8px 12px;
  }

  .announcement-actions {
    justify-content: flex-start;
  }
}
</style>
